/**
 * DDX-Plus KB ingest — Storage upload + callable wrappers (compat Functions).
 */

import { storage, functions } from './firebase';

/**
 * Ad blockers often block *.cloudfunctions.net; the browser then reports CORS / internal / failed fetch.
 * @param {unknown} err
 * @param {string} step
 */
function wrapDdxIngestError(err, step) {
  const o = err && typeof err === 'object' ? err : null;
  const code = o && 'code' in o ? String(/** @type {{ code?: unknown }} */ (o).code) : '';
  const msg =
    o && 'message' in o && /** @type {{ message?: unknown }} */ (o).message != null
      ? String(/** @type {{ message?: unknown }} */ (o).message)
      : String(err);
  const combined = `${code} ${msg}`.toLowerCase();
  const looksNetwork =
    combined.includes('cors') ||
    combined.includes('failed to fetch') ||
    combined.includes('network') ||
    combined.includes('blocked_by_client') ||
    code === 'functions/internal';
  if (!looksNetwork) return err;

  const emulatorOn = process.env.REACT_APP_USE_FUNCTIONS_EMULATOR === 'true';
  const hint = emulatorOn
    ? ' Functions emulator mode is on: confirm `firebase emulators:start --only functions` is running on 127.0.0.1:5001, use http://localhost:3000 or http://127.0.0.1:3000, restart dev after .env.local changes, and check the emulator terminal for stack traces.'
    : typeof window !== 'undefined' &&
        (window.location?.hostname === 'localhost' || window.location?.hostname === '127.0.0.1')
      ? ' On local dev: disable ad blockers (they often block cloudfunctions.net), or set REACT_APP_USE_FUNCTIONS_EMULATOR=true, run `firebase emulators:start --only functions`, and restart npm.'
      : ' Try disabling ad blockers for this site or use a profile without tracker blocking.';
  return new Error(`${step}: ${msg}.${hint}`);
}

/**
 * Upload raw ingest file under path from createDdxIngestJob (Firebase Storage rules enforce role + uid).
 *
 * @param {File|Blob} file
 * @param {string} storagePath - e.g. ddxIngest/{uid}/{jobId}/file.tsv
 * @returns {Promise<void>}
 */
export async function uploadDdxIngestRawFile(file, storagePath) {
  if (!file || !storagePath) {
    throw new Error('uploadDdxIngestRawFile: file and storagePath are required');
  }
  const ref = storage.ref().child(storagePath);
  try {
    await ref.put(file);
  } catch (err) {
    throw wrapDdxIngestError(err, 'Storage upload');
  }
}

/**
 * After exportDdxIngestStagedJsonl writes export.jsonl, resolve a browser-openable URL via Storage rules.
 *
 * @param {string} exportPath - full object path from callable
 * @returns {Promise<string>}
 */
export async function getDdxIngestExportDownloadUrl(exportPath) {
  if (!exportPath || typeof exportPath !== 'string') {
    throw new Error('getDdxIngestExportDownloadUrl: exportPath is required');
  }
  const ref = storage.ref().child(exportPath);
  try {
    return await ref.getDownloadURL();
  } catch (err) {
    throw wrapDdxIngestError(err, 'export download URL');
  }
}

/**
 * Syncs `globalLlmManager` custom claim from Firestore `users/{uid}` (server). Call then `getIdToken(true)` so Storage rules see the claim.
 *
 * @returns {Promise<{ ok?: boolean, globalLlmManager?: boolean }>}
 */
export async function callRefreshMyAuthClaims() {
  try {
    const fn = functions.httpsCallable('refreshMyAuthClaims');
    const res = await fn({});
    const data = res.data;
    if (
      data &&
      data.emulatorClaimSyncSkipped &&
      typeof console !== 'undefined' &&
      console.warn
    ) {
      console.warn(
        '[callRefreshMyAuthClaims] Emulator did not sync custom claims (Admin SDK). ' +
          'For real claim sync: run `gcloud auth application-default login`, or set REACT_APP_USE_FUNCTIONS_EMULATOR=false to use deployed functions. ' +
          `Detail: ${data.emulatorError || 'unknown'}`
      );
    }
    return data;
  } catch (err) {
    throw wrapDdxIngestError(err, 'refreshMyAuthClaims');
  }
}

/**
 * @param {{
 *   sourceFilename?: string,
 *   delimiter?: string,
 *   contentType?: string,
 *   columnMap?: { sourceByTarget: (number|null)[] },
 *   columnMapMaxSourceCols?: number,
 * }} [payload]
 * @returns {Promise<{ jobId: string, sourceStoragePath: string, sourceFilename: string }>}
 */
export async function callCreateDdxIngestJob(payload = {}) {
  try {
    const fn = functions.httpsCallable('createDdxIngestJob');
    const res = await fn(payload);
    return res.data;
  } catch (err) {
    throw wrapDdxIngestError(err, 'createDdxIngestJob');
  }
}

/**
 * @param {{ jobId: string }} payload
 */
export async function callFinalizeDdxIngestUpload(payload) {
  try {
    const fn = functions.httpsCallable('finalizeDdxIngestUpload', { timeout: 540000 });
    const res = await fn(payload);
    return res.data;
  } catch (err) {
    throw wrapDdxIngestError(err, 'finalizeDdxIngestUpload');
  }
}

/**
 * @param {{ jobId: string, dryRun?: boolean }} payload
 */
export async function callPromoteDdxIngestJob(payload) {
  try {
    const fn = functions.httpsCallable('promoteDdxIngestJob', { timeout: 540000 });
    const res = await fn(payload);
    return res.data;
  } catch (err) {
    throw wrapDdxIngestError(err, 'promoteDdxIngestJob');
  }
}

/**
 * @param {{ jobId: string }} payload
 */
export async function callExportDdxIngestStagedJsonl(payload) {
  try {
    const fn = functions.httpsCallable('exportDdxIngestStagedJsonl', { timeout: 120000 });
    const res = await fn(payload);
    return res.data;
  } catch (err) {
    throw wrapDdxIngestError(err, 'exportDdxIngestStagedJsonl');
  }
}

/**
 * Rebuilds TSV or CSV matching the job delimiter and column map from staged cleanedFields (blobs resolved).
 *
 * @param {{ jobId: string }} payload
 * @returns {Promise<{ exportPath: string, rowCount: number, delimiter: string, extension: string }>}
 */
export async function callExportDdxIngestStagedSpreadsheet(payload) {
  try {
    const fn = functions.httpsCallable('exportDdxIngestStagedSpreadsheet', { timeout: 540000 });
    const res = await fn(payload);
    return res.data;
  } catch (err) {
    throw wrapDdxIngestError(err, 'exportDdxIngestStagedSpreadsheet');
  }
}

/**
 * @param {{ jobId: string }} payload
 */
export async function callRollbackDdxIngestPromote(payload) {
  try {
    const fn = functions.httpsCallable('rollbackDdxIngestPromote', { timeout: 540000 });
    const res = await fn(payload);
    return res.data;
  } catch (err) {
    throw wrapDdxIngestError(err, 'rollbackDdxIngestPromote');
  }
}

/**
 * @param {{ chiefComplaintId?: string, differentialText?: string }} payload
 */
export async function callPreviewDdxGraphCandidates(payload) {
  try {
    const fn = functions.httpsCallable('previewDdxGraphCandidates');
    const res = await fn(payload);
    return res.data;
  } catch (err) {
    throw wrapDdxIngestError(err, 'previewDdxGraphCandidates');
  }
}

/**
 * Permanently delete ingest job, staged rows, AI findings, and related Storage objects (not live KB docs).
 *
 * @param {{ jobId: string }} payload
 */
export async function callDeleteDdxIngestJob(payload) {
  try {
    const fn = functions.httpsCallable('deleteDdxIngestJob', { timeout: 540000 });
    const res = await fn(payload);
    return res.data;
  } catch (err) {
    throw wrapDdxIngestError(err, 'deleteDdxIngestJob');
  }
}

/** @param {{ jobId: string }} payload */
export async function callTriggerDdxAiReview(payload) {
  try {
    const fn = functions.httpsCallable('triggerDdxAiReview', { timeout: 540000 });
    const res = await fn(payload);
    return res.data;
  } catch (err) {
    throw wrapDdxIngestError(err, 'triggerDdxAiReview');
  }
}

/** @param {{ jobId: string }} payload */
export async function callRetryDdxAiReview(payload) {
  try {
    const fn = functions.httpsCallable('retryDdxAiReview', { timeout: 540000 });
    const res = await fn(payload);
    return res.data;
  } catch (err) {
    throw wrapDdxIngestError(err, 'retryDdxAiReview');
  }
}

/**
 * @param {{ stagedRowDocId: string, findingId: string, reviewStatus: string, editedText?: string }} payload
 */
export async function callUpdateDdxAiFinding(payload) {
  try {
    const fn = functions.httpsCallable('updateDdxAiFinding');
    const res = await fn(payload);
    return res.data;
  } catch (err) {
    throw wrapDdxIngestError(err, 'updateDdxAiFinding');
  }
}

/**
 * @param {{ stagedRowDocId: string, findingId: string, newFieldText: string, reviewStatus: 'accepted'|'edited' }} payload
 */
export async function callApplyDdxFindingFix(payload) {
  try {
    const fn = functions.httpsCallable('applyDdxFindingFix', { timeout: 120000 });
    const res = await fn(payload);
    return res.data;
  } catch (err) {
    throw wrapDdxIngestError(err, 'applyDdxFindingFix');
  }
}

/** @param {{ stagedRowDocId: string, findingId: string }} payload */
export async function callRevertDdxAutoAppliedFix(payload) {
  try {
    const fn = functions.httpsCallable('revertDdxAutoAppliedFix', { timeout: 120000 });
    const res = await fn(payload);
    return res.data;
  } catch (err) {
    throw wrapDdxIngestError(err, 'revertDdxAutoAppliedFix');
  }
}

/** @param {{ stagedRowDocId: string, findingId: string }} payload */
export async function callGenerateGroundedDdxFindingFix(payload) {
  try {
    const fn = functions.httpsCallable('generateGroundedDdxFindingFix', { timeout: 120000 });
    const res = await fn(payload);
    return res.data;
  } catch (err) {
    throw wrapDdxIngestError(err, 'generateGroundedDdxFindingFix');
  }
}
